import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Select, Spin } from "antd";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { calculateNpsScoresByLabel } from "../../../utils/calculateNpsScore";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import NpsOverTimeScoreChart from "./NpsOverTimeScoreChart";
import { formatDateToUtc } from "../../../utils/formatDate";
import { debounce } from "lodash";

const getMetadataFields = (metadata) =>
    metadata.filter((el) => el.type === "number" || el.type === "score");

const { Option } = Select;
const getMetadataOptions = (metadataFields) =>
    metadataFields.map((field) => ({
        label: field.name,
        value: field.name,
        values: field.values,
        type: field.type,
    }));

const NpsOverTime = (props) => {
    const {
        id,
        selectedDateName,
        interval,
        metaDataQu,
        smoothLine,
        filters,
        setAdditionalFilters,
        scoreLines,
        setScoreLines,
    } = props;

    const metadataFields = useMemo(
        () =>
            getMetadataFields(
                metaDataQu.metaData.filter((value) => value.values && value.values.length > 0 && value.values.length < 20),
            ),
        [metaDataQu.metaData],
    );

    const [loading, setLoading] = useState(false);
    const [npsData, setNpsData] = useState([]);
    const metadataOptions = useMemo(() => getMetadataOptions(metadataFields), [metadataFields]);
    const [driverValues, setDriverValues] = useState([]);

    useEffect(() => {
        if (metaDataQu?.metaDataDrivers?.length > 0) {
            setDriverValues(metaDataQu.metaDataDrivers.filter((value) => value.values && value.values.length > 0 && value.values.length < 20));
            setScoreLines((prevLines) => {
                if (prevLines.length > 0) return prevLines;
                const defaultDriver = metaDataQu.metaDataDrivers[0] || {};
                return [
                    {
                        id: 1,
                        driver: defaultDriver.name || null,
                    },
                ];
            });
        }
    }, [metaDataQu]);

    const conceptFilter = useMemo(
        () => JSON.stringify(getConceptFilter(props)),
        [props],
    );

    const [getVolume] = useGetConceptsFiltersMutation();

    const fetchAllLinesData = useCallback(async () => {
        setLoading(true);

        try {
            const batchSize = 5;
            const allLinesData = [];

            for (const line of scoreLines) {
                const selectedOption = metadataOptions.find((option) => option.label === line.driver) || metadataOptions[0];
                if (!selectedOption) continue;
                const batches = [];
                for (let i = 0; i < selectedOption.values.length; i += batchSize) {
                    batches.push(selectedOption.values.slice(i, i + batchSize));
                }
                let combinedData = [];
                for (const batch of batches) {
                    const batchPromises = batch.map((value) => {
                        const filterScore = selectedOption.type === "number"
                            ? [
                                {
                                    name: selectedOption.value,
                                    minimum: Number(value.value),
                                    maximum: Number(value.value),
                                },
                            ]
                            : [
                                {
                                    name: selectedOption.value,
                                    values: [value.value],
                                },
                            ];

                        return getVolume({
                            id,
                            concept_selector: JSON.parse(conceptFilter),
                            filter: [...filterScore, ...filters],
                            breakdowns: [{ name: selectedDateName, interval }],
                        }).then((response) => ({
                            value: value.value,
                            data: response.data,
                            lineId: line.id,
                            driver: line.driver,
                        }));
                    });

                    const results = await Promise.all(batchPromises);

                    const batchCombinedData = results.map((result) => ({
                        driver: result.driver,
                        value: result.value,
                        lineId: result.lineId,
                        relatedData: result.data?.breakdowns[0]?.buckets.map((bucket) => ({
                            filter_count: bucket.filter_count,
                            total_count: bucket.total_count,
                            label: formatDateToUtc(bucket.label, interval),
                        })),
                    }));
                    combinedData = combinedData.concat(batchCombinedData);
                }

                allLinesData.push(...combinedData);
            }
            // Combine data by label and lineId
            const combinedByLabel = allLinesData.reduce((acc, { value, lineId, relatedData, driver  }) => {
                relatedData.forEach(({ label, filter_count, total_count }) => {
                    let labelEntry = acc.find((entry) => entry.label === label);
                    if (!labelEntry) {
                        labelEntry = { label, lines: {} };
                        acc.push(labelEntry);
                    }
                    if (!labelEntry.lines[lineId]) {
                        labelEntry.lines[lineId] = { data: [], driver };
                    }

                    labelEntry.lines[lineId].data.push({ value, filter_count, total_count });
                });
                return acc;
            }, []);

            const result = combinedByLabel.map(({ label, lines }) => {
                const lineScores = Object.keys(lines).map((lineId) => {
                    const scores = calculateNpsScoresByLabel([{ label, values: lines[lineId].data }]);
                    return {
                        lineId: Number(lineId),
                        score: scores[0]?.nps_score || 0,
                        driver: lines[lineId].driver,
                    };
                });
                return {
                    label,
                    lines: lineScores,
                };
            });

            setNpsData(result);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("Error fetching line data:", error);
        }
    }, [scoreLines, metadataOptions, conceptFilter, selectedDateName, interval, filters]);

    useEffect(() => {
        setLoading(true);
        const debouncedGetData = debounce(fetchAllLinesData, 1000);
        debouncedGetData();
        return () => {
            debouncedGetData.cancel();
        };
    }, [fetchAllLinesData]);

    const handleDriverChange = (lineId, driverName) => {
        setScoreLines((prevLines) =>
            prevLines.map((l) => {
                if (l.id === lineId) {
                    return {
                        ...l,
                        driver: driverName,
                    };
                }
                return l;
            })
        );
    };

    const addLine = () => {
        setScoreLines((prevLines) => {
            const newId = prevLines.length > 0 ? prevLines[prevLines.length - 1].id + 1 : 1;
            const selectedDrivers = prevLines.map((line) => line.driver);
            const availableDriver = driverValues.find(
                (driver) => !selectedDrivers.includes(driver.name)
            );
            if (!availableDriver) return prevLines;
            return [
                ...prevLines,
                {
                    id: newId,
                    driver: availableDriver.name,
                },
            ];
        });
    };

    const removeLine = (lineId) => {
        setScoreLines((prevLines) => prevLines.filter((line) => line.id !== lineId));
    };

    useEffect(() => {
        setAdditionalFilters?.([
            {
                key: "filterScore",
                label: "Score Filter",
                showArrow: false,
                children: (
                    <ScoreFilterComponent
                        scoreLines={scoreLines}
                        driverValues={driverValues}
                        handleDriverChange={handleDriverChange}
                        addLine={addLine}
                        removeLine={removeLine}
                    />
                ),
            },
        ]);
    }, [scoreLines]);

    if (loading) {
        return (
            <Spin
                className="spinner-by-center"
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }

    const height = filters.length > 0 ? props.widget.layout.h * 39 - 100 : props.widget.layout.h * 39 - 30;
    return (
        <div
            style={{ height }}
            onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
        >
            <NpsOverTimeScoreChart
                data={npsData}
                smoothLine={smoothLine}
            />
        </div>
    );
};

export default NpsOverTime;


const ScoreFilterComponent = ({
                                  scoreLines,
                                  driverValues,
                                  handleDriverChange,
                                  addLine,
                                  removeLine,
                              }) => {
    const selectedDrivers = scoreLines.map((line) => line.driver);
    const allDriversSelected = driverValues.every((driver) =>
        selectedDrivers.includes(driver.name)
    );
    return (
        <div>
            {scoreLines.map((line) => (
                <div
                    key={line.id}
                    style={{
                        borderBottom: "1px dashed #ccc",
                        paddingBottom: 10,
                        marginBottom: 10,
                        display: "flex",
                        gap: 8,
                        alignItems: 'stretch',
                        flexDirection: 'column'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <span style={{ fontWeight: 600, marginRight: 8 }}>
                        Line {line.driver}
                    </span>
                        {scoreLines.length > 1 && (
                            <Button
                                type="link"
                                danger
                                onClick={() => removeLine(line.id)}
                                icon={<CloseOutlined style={{ fontSize: 12 }} />}
                            />
                        )}
                    </div>

                    <Select
                        style={{ flex: 1, marginBottom: 10 }}
                        placeholder="Select a driver"
                        value={line.driver}
                        onChange={(value) => handleDriverChange(line.id, value)}
                    >
                        {driverValues.map((driver) => (
                            <Option
                                key={driver.name}
                                value={driver.name}
                                disabled={selectedDrivers.includes(driver.name)}
                            >
                                {driver.name}
                            </Option>
                        ))}
                    </Select>

                </div>
            ))}
            {!allDriversSelected && (
                <Button type="dashed" onClick={addLine} block>
                    + Add line
                </Button>
            )}
        </div>
    );
};
